'use client';

const USER_ID_KEY = 'autofinplan_user_id';

/**
 * Generates a UUID v4
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Gets or creates a unique user ID that persists in localStorage
 * This ID is used to track anonymous users across sessions
 */
export function getUserId(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    // Try to get existing ID from localStorage
    let userId = localStorage.getItem(USER_ID_KEY);

    if (!userId) {
      // Generate new UUID if none exists
      userId = generateUUID();
      localStorage.setItem(USER_ID_KEY, userId);
    }

    return userId;
  } catch (_error) {
    // If localStorage is not available (private browsing, etc.)
    // generate a session-only ID
    console.warn('localStorage not available, using session-only ID');
    return generateUUID();
  }
}

/**
 * Updates the user ID (used when user signs up/logs in to connect anonymous and authenticated sessions)
 */
export function setUserId(userId: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(USER_ID_KEY, userId);
  } catch (_error) {
    console.warn('Failed to store user ID');
  }
}

/**
 * Gets additional browser fingerprint data to enhance user tracking accuracy
 */
export function getBrowserFingerprint(): {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  timezone: string;
  cookieEnabled: boolean;
} {
  if (typeof window === 'undefined') {
    return {
      userAgent: '',
      language: '',
      platform: '',
      screenResolution: '',
      timezone: '',
      cookieEnabled: false
    };
  }

  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookieEnabled: navigator.cookieEnabled
  };
}
